import apiClient from './api';
import { store } from '../store/store';
import { dedupeAsync } from '../utils/requestDedupe';
import type {
  GameType,
  GameEligibility,
  GameTodayResult,
  SpinResult,
  StopClockStartResponse,
  StopClockPingResponse,
  StopClockStopResponse,
  BillSplitResponse,
  RedeemResponse,
} from '../types/games';

function guestToken(): string {
  const key = 'peppar_guest_token';
  let token = localStorage.getItem(key);
  if (!token) {
    token = crypto.randomUUID();
    localStorage.setItem(key, token);
  }
  return token;
}

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function playerKey(): string {
  const user = store.getState().user.user;
  if (user?.id != null) {
    return `user-${user.id}`;
  }
  return guestToken();
}

class GamesService {
  /** Stable per player/outlet/day — prevents duplicate awards and pairs with server idempotency. */
  dailyIdempotencyKey(outletId: number, gameType: GameType): string {
    return `${outletId}-${gameType}-${todayKey()}-${playerKey()}`;
  }

  /** One-time Google Review claim per player at this restaurant. */
  lifetimeIdempotencyKey(outletId: number, gameType: GameType): string {
    return `${outletId}-${gameType}-lifetime-${playerKey()}`;
  }

  async getEnabledGames(appName: string): Promise<{ gameTypes: GameType[] }> {
    return dedupeAsync(`games-enabled:${appName}`, async () => {
      const response = await apiClient.get(`/api/restaurants/${appName}/games/enabled`);
      return response.data as { gameTypes: GameType[] };
    }, 30_000);
  }

  async getEligibility(appName: string, gameType: GameType): Promise<GameEligibility> {
    const response = await apiClient.get(
      `/api/restaurants/${appName}/games/eligibility`,
      { params: { gameType } }
    );
    return response.data as GameEligibility;
  }

  async getTodayResults(appName: string): Promise<{ plays: GameTodayResult[] }> {
    const response = await apiClient.get(
      `/api/restaurants/${appName}/games/today-results`
    );
    return response.data as { plays: GameTodayResult[] };
  }

  async spin(appName: string, gameType: GameType, idempotencyKey: string): Promise<SpinResult> {
    const response = await apiClient.post(`/api/restaurants/${appName}/games/spin`, {
      gameType,
      idempotencyKey,
    });
    return response.data as SpinResult;
  }

  async stopClockStart(appName: string): Promise<StopClockStartResponse> {
    const response = await apiClient.post(`/api/restaurants/${appName}/games/stop-clock/start`, {});
    return response.data as StopClockStartResponse;
  }

  async stopClockPing(appName: string, sessionToken: string): Promise<StopClockPingResponse> {
    const response = await apiClient.post(`/api/restaurants/${appName}/games/stop-clock/ping`, {
      sessionToken,
    });
    return response.data as StopClockPingResponse;
  }

  async stopClockStop(appName: string, sessionToken: string): Promise<StopClockStopResponse> {
    const response = await apiClient.post(`/api/restaurants/${appName}/games/stop-clock/stop`, {
      sessionToken,
    });
    return response.data as StopClockStopResponse;
  }

  async billSplitSpin(
    appName: string,
    tableSessionId: number,
    names: string[]
  ): Promise<BillSplitResponse> {
    const response = await apiClient.post(`/api/restaurants/${appName}/games/bill-split/spin`, {
      tableSessionId,
      names,
    });
    return response.data as BillSplitResponse;
  }

  async claimGoogleReview(
    appName: string,
    idempotencyKey: string,
    userReviewLink: string
  ): Promise<SpinResult> {
    const response = await apiClient.post(`/api/restaurants/${appName}/games/google-review/claim`, {
      idempotencyKey,
      userReviewLink,
    });
    return response.data as SpinResult;
  }

  async redeemCode(appName: string, redemptionCode: string, orderId?: number): Promise<RedeemResponse> {
    const response = await apiClient.post(`/api/restaurants/${appName}/games/redeem`, {
      redemptionCode,
      orderId,
    });
    return response.data as RedeemResponse;
  }
}

const gamesService = new GamesService();
export default gamesService;
